import React, { useState, useEffect } from 'react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/select';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '../ui/collapsible';
import { ChevronDown, ChevronUp, Filter, X } from 'lucide-react';
import type { ExpenseFilter } from '../../types';
import { useAssetTypes } from '../../hooks/useAssetTypes';
import { useAssets } from '../../hooks/useAssets';
import { useUsers } from '../../hooks/useUsers';
import { usePermissions } from '../../hooks/usePermissions';

interface ExpenseFiltersProps {
  filters: ExpenseFilter;
  onFilterChange: (filters: ExpenseFilter) => void;
  onReset: () => void;
  projects: Array<{ projectId: string; name: string }>;
  categories: Array<{ categoryId: string; name: string }>;
}

const ExpenseFilters: React.FC<ExpenseFiltersProps> = ({
  filters,
  onFilterChange,
  onReset,
  projects,
  categories,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  
  // Permissions
  const { canManageUsers } = usePermissions();
  
  // Fetch users for admin filter
  const { data: users = [] } = useUsers({ activeOnly: true });
  
  // Fetch asset types (active only)
  const { data: assetTypes = [] } = useAssetTypes(true);
  
  // Fetch assets filtered by selected asset type
  const { data: assets = [] } = useAssets({
    assetTypeId: filters.assetTypeId,
    activeOnly: true,
  });
  
  // Reset assetId when assetTypeId changes
  useEffect(() => {
    if (filters.assetTypeId && filters.assetId) {
      // Check if the current assetId belongs to the selected assetTypeId
      const assetBelongsToType = assets.some(
        (asset) => asset.assetId === filters.assetId
      );
      if (!assetBelongsToType) {
        handleChange('assetId', undefined);
      }
    }
  }, [filters.assetTypeId, assets]);
  
  const hasActiveFilters = Object.keys(filters).some(key => {
    const value = filters[key as keyof ExpenseFilter];
    return value !== undefined && value !== '' && value !== '__all__';
  });

  const activeFilterCount = Object.keys(filters).filter(key => {
    const value = filters[key as keyof ExpenseFilter];
    return value !== undefined && value !== '' && value !== '__all__';
  }).length;

  const handleChange = (field: keyof ExpenseFilter, value: any) => {
    onFilterChange({
      ...filters,
      [field]: value === '__all__' ? undefined : value,
    });
  };

  return (
    <Collapsible open={isOpen} onOpenChange={setIsOpen} className="mb-6">
      <div className="flex items-center justify-between gap-4">
        <CollapsibleTrigger asChild>
          <Button variant="outline" className="gap-2">
            <Filter className="h-4 w-4" />
            Filtros
            {hasActiveFilters && (
              <span className="ml-1 rounded-full bg-blue-600 px-2 py-0.5 text-xs text-white">
                {activeFilterCount}
              </span>
            )}
            {isOpen ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
          </Button>
        </CollapsibleTrigger>
        
        {hasActiveFilters && (
          <Button variant="ghost" size="sm" onClick={onReset} className="gap-2">
            <X className="h-4 w-4" />
            Limpiar filtros
          </Button>
        )}
      </div>

      <CollapsibleContent className="mt-4">
        <div className="rounded-lg border bg-card p-4">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            {/* Búsqueda */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Buscar
              </label>
              <Input
                type="text"
                value={filters.search || ''}
                onChange={(e) => handleChange('search', e.target.value)}
                placeholder="Descripción o proveedor..."
              />
            </div>

            {/* Proyecto */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Proyecto
              </label>
              <Select
                value={filters.projectId || '__all__'}
                onValueChange={(value) => handleChange('projectId', value)}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Todos los proyectos" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="__all__">Todos los proyectos</SelectItem>
                  {projects.map((project) => (
                    <SelectItem key={project.projectId} value={project.projectId}>
                      {project.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Categoría */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Categoría
              </label>
              <Select
                value={filters.categoryId || '__all__'}
                onValueChange={(value) => handleChange('categoryId', value)}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Todas las categorías" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="__all__">Todas las categorías</SelectItem>
                  {categories.map((category) => (
                    <SelectItem key={category.categoryId} value={category.categoryId}>
                      {category.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Proveedor */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Proveedor
              </label>
              <Input
                type="text"
                value={filters.provider || ''}
                onChange={(e) => handleChange('provider', e.target.value)}
                placeholder="Nombre del proveedor..."
              />
            </div>

            {/* Fecha desde */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Desde
              </label>
              <Input
                type="date"
                value={filters.startDate || ''}
                onChange={(e) => handleChange('startDate', e.target.value)}
              />
            </div>

            {/* Fecha hasta */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Hasta
              </label>
              <Input
                type="date"
                value={filters.endDate || ''}
                onChange={(e) => handleChange('endDate', e.target.value)}
              />
            </div>

            {/* Monto mínimo */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Monto mínimo
              </label>
              <Input
                type="number"
                value={filters.minAmount || ''}
                onChange={(e) => handleChange('minAmount', e.target.value ? Number(e.target.value) : undefined)}
                placeholder="0"
                min="0"
              />
            </div>

            {/* Monto máximo */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Monto máximo
              </label>
              <Input
                type="number"
                value={filters.maxAmount || ''}
                onChange={(e) => handleChange('maxAmount', e.target.value ? Number(e.target.value) : undefined)}
                placeholder="Sin límite"
                min="0"
              />
            </div>

            {/* Tipo de Activo */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Tipo de Activo
              </label>
              <Select
                value={filters.assetTypeId || '__all__'}
                onValueChange={(value) => {
                  handleChange('assetTypeId', value);
                  // Reset assetId when changing asset type
                  if (value === '__all__') {
                    handleChange('assetId', undefined);
                  }
                }}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Todos los tipos" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="__all__">Todos los tipos</SelectItem>
                  {assetTypes.map((assetType) => (
                    <SelectItem key={assetType.assetTypeId} value={assetType.assetTypeId}>
                      {assetType.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Activo Específico */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Activo Específico
              </label>
              <Select
                value={filters.assetId || '__all__'}
                onValueChange={(value) => handleChange('assetId', value)}
                disabled={!filters.assetTypeId || filters.assetTypeId === '__all__'}
              >
                <SelectTrigger>
                  <SelectValue placeholder={
                    !filters.assetTypeId || filters.assetTypeId === '__all__'
                      ? 'Seleccione un tipo primero'
                      : 'Todos los activos'
                  } />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="__all__">Todos los activos</SelectItem>
                  {assets.map((asset) => (
                    <SelectItem key={asset.assetId} value={asset.assetId}>
                      {asset.displayName}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Usuario (admin only) */}
            {canManageUsers && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Usuario
                </label>
                <Select
                  value={filters.userId || '__all__'}
                  onValueChange={(value) => handleChange('userId', value)}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Todos los usuarios" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__all__">Todos los usuarios</SelectItem>
                    {users.map((user) => (
                      <SelectItem key={user.userId} value={user.userId}>
                        {user.firstName} {user.lastName} ({user.email})
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
          </div>
        </div>
      </CollapsibleContent>
    </Collapsible>
  );
};

export default ExpenseFilters;
